import { z } from 'zod';
/**
 * Interface interna padronizada para conectores de ERP.
 * Os adaptadores reais (Fase 4) só serão implementados com documentação oficial,
 * ambiente de teste e credenciais autorizadas pelo fornecedor/provedor.
 */
export const ERP_OPERATIONS = [
  'identificar_cliente',
  'consultar_contratos',
  'consultar_faturas',
  'obter_segunda_via',
  'consultar_conexao',
  'consultar_incidentes',
  'abrir_chamado',
  'consultar_chamados',
] as const;
export type ErpOperation = (typeof ERP_OPERATIONS)[number];

export type Sensitivity = 'baixa' | 'média' | 'alta';

export const ERP_OPERATION_META: Record<
  ErpOperation,
  { label: string; description: string; sideEffect: boolean; sensitivity: Sensitivity }
> = {
  identificar_cliente: {
    label: 'Identificar cliente',
    description: 'Localiza o cliente a partir do número de WhatsApp e confirma a identidade.',
    sideEffect: false,
    sensitivity: 'alta',
  },
  consultar_contratos: {
    label: 'Consultar contratos',
    description: 'Lista contratos ativos do cliente autenticado.',
    sideEffect: false,
    sensitivity: 'média',
  },
  consultar_faturas: {
    label: 'Consultar faturas',
    description: 'Faturas em aberto e pagas do contrato autorizado.',
    sideEffect: false,
    sensitivity: 'alta',
  },
  obter_segunda_via: {
    label: 'Obter segunda via',
    description: 'Link ou linha digitável da fatura do contrato autorizado.',
    sideEffect: false,
    sensitivity: 'alta',
  },
  consultar_conexao: {
    label: 'Consultar conexão',
    description: 'Estado da sessão PPPoE e da ONU do contrato autorizado.',
    sideEffect: false,
    sensitivity: 'média',
  },
  consultar_incidentes: {
    label: 'Consultar incidentes',
    description: 'Manutenções e falhas massivas na região do cliente.',
    sideEffect: false,
    sensitivity: 'baixa',
  },
  abrir_chamado: {
    label: 'Abrir chamado',
    description: 'Registra ordem de serviço no ERP. Exige confirmação do cliente.',
    sideEffect: true,
    sensitivity: 'média',
  },
  consultar_chamados: {
    label: 'Consultar chamados',
    description: 'Chamados abertos do contrato autorizado.',
    sideEffect: false,
    sensitivity: 'média',
  },
};

/**
 * Conectores de ERP formam um CATÁLOGO gerido no Master (tabela erp_connectors): a equipe
 * cadastra novos sistemas (ex.: Voalle) sem alterar código. Cadastrar não basta para atender:
 * só um conector HOMOLOGADO libera operações, e a homologação exige um adaptador real
 * implementado no motor (packages/engine/src/erp-adapters.ts) e aprovado nos testes de contrato.
 */
export const erpConnectorKeySchema = z
  .string()
  .trim()
  .regex(/^[a-z][a-z0-9-]{1,39}$/, 'Use letras minúsculas, números e hífen (ex.: voalle)');

export type ConnectorCertification = 'nao_homologado' | 'em_homologacao' | 'homologado';
export const CONNECTOR_CERTIFICATIONS = ['nao_homologado', 'em_homologacao', 'homologado'] as const;

/** Etapas da homologação marcadas pela equipe. "Adaptador implementado" é verificado no código. */
export const HOMOLOGATION_STEPS = ['documentacao_oficial', 'ambiente_teste', 'credenciais_teste', 'testes_contrato'] as const;
export type HomologationStep = (typeof HOMOLOGATION_STEPS)[number];

export const HOMOLOGATION_STEP_META: Record<HomologationStep, { label: string; description: string; requiredFor: ConnectorCertification }> = {
  documentacao_oficial: {
    label: 'Documentação oficial da API',
    description: 'Documentação fornecida ou publicada pelo fabricante do ERP, com os endpoints usados.',
    requiredFor: 'em_homologacao',
  },
  ambiente_teste: {
    label: 'Ambiente de teste',
    description: 'Base de homologação/sandbox do ERP, separada da produção de qualquer provedor.',
    requiredFor: 'em_homologacao',
  },
  credenciais_teste: {
    label: 'Credenciais de teste autorizadas',
    description: 'Acesso de teste concedido formalmente pelo fabricante ou por um provedor parceiro.',
    requiredFor: 'em_homologacao',
  },
  testes_contrato: {
    label: 'Testes de contrato aprovados',
    description: 'Cada operação suportada validada contra o ambiente de teste, incluindo erros e limites.',
    requiredFor: 'homologado',
  },
};

/** Catálogo inicial (inserido uma vez; depois é editável no Master). */
export const DEFAULT_ERP_CONNECTORS: { key: string; name: string; vendor: string; notes: string }[] = [
  { key: 'ixc', name: 'IXC Provedor', vendor: 'IXC Soft', notes: '' },
  { key: 'hubsoft', name: 'HubSoft', vendor: 'HubSoft', notes: '' },
  { key: 'sgp', name: 'SGP', vendor: 'SGP', notes: '' },
  { key: 'mkauth', name: 'MK-Auth', vendor: 'MK-Auth', notes: '' },
];

export const CERTIFICATION_LABELS: Record<ConnectorCertification, string> = {
  nao_homologado: 'Não homologado',
  em_homologacao: 'Em homologação',
  homologado: 'Homologado',
};
